(() => {
  if (customElements.get('ac-campaign-film')) return;

  // Interactive can outlive DOMContentLoaded while third-party resources load.
  let contentLoaded = document.readyState === 'complete'
    || performance.getEntriesByType('navigation')[0]?.domContentLoadedEventStart > 0;
  document.addEventListener('DOMContentLoaded', () => { contentLoaded = true; }, { once: true });

  class ACCampaignFilm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.panels = [...this.querySelectorAll('[data-film-panel]')];
      this.choices = [...this.querySelectorAll('[data-film-choice]')];
      this.seek = this.querySelector('[data-film-seek]');
      this.fullButton = this.querySelector('[data-film-full]');
      this.controls = this.querySelector('[data-film-controls]');
      this.status = this.querySelector('[data-film-status]');
      this.activeIndex = 0;
      this.visible = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.layerId = `campaign-${this.id}`;
      this.full = false;
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.stage = this.querySelector('.ac-campaign-film__stage');
      this.scene = this.querySelector('.ac-campaign-film__scene');
      this.prepareCopy();
      const waitForMotion = !contentLoaded && (document.readyState === 'loading' || !window.ScrollTrigger);
      this.classList.toggle('is-motion-pending', waitForMotion && this.dataset.motion === 'true'
        && !this.reduced.matches && document.body.dataset.acMotion !== 'reduced');
      this.classList.add('is-enhanced');
      for (const panel of this.panels) {
        const video = panel.querySelector('.ac-campaign-film__video');
        video.controls = false;
        video.removeAttribute('src');
        video.addEventListener('timeupdate', () => this.updateProgress(video), { signal });
        video.addEventListener('play', () => this.updateTransport(), { signal });
        video.addEventListener('pause', () => this.updateTransport(), { signal });
        video.addEventListener('loadedmetadata', () => {
          if (video.videoWidth && video.videoHeight) panel.style.setProperty('--film-native-ratio', video.videoWidth / video.videoHeight);
          this.updateTransport();
        }, { signal });
        for (const media of panel.querySelectorAll('video')) {
          media.addEventListener('error', () => {
            if (panel !== this.panels[this.activeIndex]) return;
            media.pause();
            this.updateTransport();
          }, { signal });
        }
      }
      this.addEventListener('click', event => {
        const choice = event.target.closest('[data-film-choice]');
        if (choice) { this.selectFilm(Number(choice.dataset.filmChoice)); return; }
        if (event.target.closest('input, video[controls]')) return;
        if (!this.classList.contains('is-revealed') || this.transitioning) return;
        if (!event.target.closest('.ac-campaign-film__frame, [data-film-full]') && !this.full) return;
        if (this.full && this.classList.contains('is-idle')) { this.showControls(); return; }
        this.setFull(!this.full);
      }, { signal });
      this.addEventListener('pointermove', event => {
        if (!this.finePointer.matches) return;
        this.pointer = { x: event.clientX, y: event.clientY };
        this.showControls();
        const overFilm = Boolean(event.target.closest('.ac-campaign-film__frame'));
        this.classList.toggle('has-pointer', overFilm);
        if (!overFilm) return;
        if (this.cursorFrame) return;
        this.cursorFrame = requestAnimationFrame(() => {
          this.cursorFrame = 0;
          this.fullButton.style.setProperty('--cursor-x', `${this.pointer.x}px`);
          this.fullButton.style.setProperty('--cursor-y', `${this.pointer.y}px`);
        });
      }, { signal });
      this.addEventListener('pointerleave', () => this.classList.remove('has-pointer'), { signal });
      this.addEventListener('pointerdown', () => {
        // The first tap on an idle full film reveals controls instead of closing it.
        if (this.full && this.classList.contains('is-idle')) return;
        this.showControls();
      }, { signal });
      this.addEventListener('keydown', event => {
        this.showControls();
        if (event.key === ' ' && this.full && event.target === this.frame) {
          event.preventDefault();
          this.video.paused ? this.video.play().catch(() => this.updateTransport()) : this.video.pause();
        }
      }, { signal });
      this.seek?.addEventListener('input', () => {
        if (!Number.isFinite(this.video?.duration)) return;
        const time = Number(this.seek.value) / 100 * (this.full ? this.video.duration : Math.min(15, this.video.duration));
        this.video.currentTime = time;
        this.seek.parentElement.style.setProperty('--film-progress', Number(this.seek.value) / 100);
        if (this.background.readyState > 0) this.background.currentTime = time;
      }, { signal });
      const syncMotionPreference = () => {
        this.setupMotion();
      };
      this.reduced.addEventListener('change', syncMotionPreference, { signal });
      this.motionObserver = new MutationObserver(syncMotionPreference);
      this.motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
      document.addEventListener('visibilitychange', () => {
        this.syncPlayback();
      }, { signal });
      document.addEventListener('ac:layer:change', () => this.syncPlayback(), { signal });
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-film-panel]');
        if (!panel) return;
        this.selectFilm(Number(panel.dataset.filmPanel));
        if (this.trigger) window.AcSmoothScroll?.lenis?.scrollTo(this.trigger.start + (this.trigger.end - this.trigger.start) * .5, { immediate: true });
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.syncPlayback();
      }, { threshold: .1 });
      this.observer.observe(this.stage);
      this.selectFilm(0);
      if (waitForMotion) {
        document.addEventListener('DOMContentLoaded', () => this.setupMotion(), { once: true, signal });
      } else this.setupMotion();
    }

    get video() { return this.panels[this.activeIndex]?.querySelector('.ac-campaign-film__video'); }
    get background() { return this.panels[this.activeIndex]?.querySelector('.ac-campaign-film__background'); }
    get frame() { return this.panels[this.activeIndex]?.querySelector('.ac-campaign-film__frame'); }

    prepareCopy() {
      // Blend normal text against the video, not its red emphasis. Keep one richtext source.
      for (const copy of this.querySelectorAll('.ac-campaign-film__copy')) {
        const walker = document.createTreeWalker(copy, NodeFilter.SHOW_TEXT);
        const nodes = [];
        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.textContent.trim() && !node.parentElement.closest('strong, b, .ac-campaign-film__text')) nodes.push(node);
        }
        for (const node of nodes) {
          const span = document.createElement('span');
          span.className = 'ac-campaign-film__text';
          node.replaceWith(span);
          span.append(node);
        }
      }
    }

    setupMotion() {
      this.motion?.revert();
      this.motion = null;
      this.trigger = null;
      this.classList.remove('is-motion');
      this.setRevealed(true);
      const { gsap, ScrollTrigger } = window;
      if (gsap && ScrollTrigger && this.dataset.motion === 'true' && document.body.dataset.acMotion !== 'reduced') {
        gsap.registerPlugin(ScrollTrigger);
        this.motion = gsap.matchMedia();
        this.motion.add('(prefers-reduced-motion: no-preference)', () => {
          this.classList.add('is-motion');
          const scene = this.querySelector('.ac-campaign-film__scene');
          const footer = document.querySelector('ac-reference-footer');
          const update = trigger => {
            const [from, to] = innerWidth < 768 ? [90, 1000] : innerWidth < 1024 ? [60, 500] : [30, 440];
            const distance = trigger.end - trigger.start;
            // Match the homepage mask's full scroll range; pinning continues behind the footer.
            const progress = footer ? gsap.utils.clamp(0, 1, trigger.progress * distance / Math.max(1, distance - innerHeight)) : trigger.progress;
            scene.style.maskSize = `${from + (to - from) * progress}% auto`;
            this.setRevealed(progress >= .4);
          };
          this.trigger = ScrollTrigger.create({
            trigger: this,
            start: 'top top',
            endTrigger: footer || this,
            end: footer ? 'bottom top' : 'bottom bottom',
            pin: this.querySelector('.ac-campaign-film__stage'),
            pinSpacing: false,
            invalidateOnRefresh: true,
            onUpdate: update,
            onRefresh: update
          });
          update(this.trigger);
          return () => {
            scene.style.removeProperty('mask-size');
            this.classList.remove('is-motion');
            this.setRevealed(true);
          };
        }, this);
      }
      this.classList.remove('is-motion-pending');
      this.syncPlayback();
    }

    setRevealed(revealed) {
      this.classList.toggle('is-revealed', revealed);
      if (this.controls) this.controls.inert = !revealed;
      if (this.fullButton) this.fullButton.inert = !revealed;
    }

    selectFilm(index) {
      if (!this.panels[index] || this.full || this.transitioning) return;
      const changed = this.activeIndex !== index;
      if (changed) this.dataset.filmSwitching = 'true';
      for (const [i, panel] of this.panels.entries()) {
        for (const video of panel.querySelectorAll('video')) video.pause();
        panel.classList.toggle('is-active', i === index);
        panel.inert = i !== index;
        panel.setAttribute('aria-hidden', String(i !== index));
      }
      this.activeIndex = index;
      if (!this.video.getAttribute('src') && this.video.dataset.previewSrc) this.video.src = this.video.dataset.previewSrc;
      for (const [i, button] of this.choices.entries()) button.setAttribute('aria-pressed', String(i === index));
      if (changed) {
        this.video.currentTime = 0;
        if (this.background.readyState > 0) this.background.currentTime = 0;
        if (this.seek) this.seek.value = '0';
        this.seek?.parentElement.style.setProperty('--film-progress', 0);
      }
      this.syncPlayback();
    }

    syncPlayback() {
      if (!this.controller || !this.video) return;
      const automatic = this.dataset.autoplay === 'true' && !this.reduced.matches && document.body.dataset.acMotion !== 'reduced';
      const playing = !this.video.error && this.visible && !document.hidden && (this.full || !window.AcLayerManager?.isLocked()) && (this.full || automatic);
      for (const video of [this.video, this.background]) {
        if (video.error) continue;
        if (this.full && video === this.background) { video.pause(); continue; }
        if (!playing) { video.pause(); continue; }
        if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
        if (video.getAttribute('src')) video.play().catch(() => this.updateTransport());
      }
      this.updateTransport();
    }

    updateProgress(video) {
      if (video !== this.video) return;
      const duration = this.full ? video.duration : Math.min(15, video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return;
      if (!this.full && !video.paused && video.currentTime >= duration) {
        video.currentTime = 0;
        if (this.background.readyState > 0) this.background.currentTime = 0;
      }
      if (this.seek) this.seek.value = String(video.currentTime / duration * 100);
      this.seek?.parentElement.style.setProperty('--film-progress', video.currentTime / duration);
    }

    updateTransport() {
      if (!this.controller) return;
      this.classList.toggle('is-playing', Boolean(this.video && !this.video.paused));
      const available = Boolean(this.video?.getAttribute('src'));
      this.fullButton.disabled = !this.video?.dataset.fullSrc;
      this.seek.disabled = !available || !Number.isFinite(this.video.duration);
      const failed = Boolean(this.video?.error);
      if (this.status) this.status.textContent = failed ? this.status.dataset.message : '';
      // Native controls are a functional failure fallback, not permanent visual chrome.
      this.video.controls = failed;
    }

    showControls() {
      this.classList.remove('is-idle');
      clearTimeout(this.idleTimer);
      if (this.full) this.idleTimer = setTimeout(() => this.classList.add('is-idle'), 3000);
    }

    async setFull(full) {
      if (!this.video?.dataset.fullSrc || this.full === full || this.transitioning) return;
      this.transitioning = true;
      this.full = full;
      this.fullButton.querySelector('span').textContent = `[ ${full ? this.fullButton.dataset.closeLabel : this.fullButton.dataset.openLabel} ]`;
      this.classList.toggle('is-full', full);
      this.controls.querySelector('.ac-campaign-film__choices').inert = full;
      this.showControls();
      if (full) {
        // Isolate sibling branches without making the in-content player inert.
        window.AcLayerManager?.acquire(this.layerId, {
          opener: this.fullButton,
          getFocusRoots: () => [this.frame, this.fullButton, this.seek],
          getInertRoots: () => {
            const roots = [];
            for (let node = this; node.parentElement && node !== document.body; node = node.parentElement) {
              roots.push(...[...node.parentElement.children].filter(child => child !== node && child instanceof HTMLElement));
            }
            return roots;
          },
          onEscape: () => this.setFull(false)
        });
        this.frame.setAttribute('role', 'dialog');
        this.frame.setAttribute('aria-modal', 'true');
        this.frame.setAttribute('aria-keyshortcuts', 'Space Escape');
        this.frame.focus({ preventScroll: true });
        if (this.video.dataset.fullSrc !== this.video.dataset.previewSrc) this.video.src = this.video.dataset.fullSrc;
        this.video.currentTime = 0;
        this.video.muted = false;
        this.video.loop = false;
      } else {
        this.frame.removeAttribute('role');
        this.frame.removeAttribute('aria-modal');
        this.frame.removeAttribute('aria-keyshortcuts');
        this.video.muted = true;
        this.video.loop = true;
        if (this.video.dataset.fullSrc !== this.video.dataset.previewSrc) this.video.src = this.video.dataset.previewSrc;
        if (this.video.currentTime > 15) this.video.currentTime = 0;
      }
      this.syncPlayback();
      // A finite inset transition keeps the player undistorted; no duplicate video or RAF layout loop.
      getComputedStyle(this.frame).inset;
      await Promise.all(this.frame.getAnimations().map(animation => animation.finished.catch(() => {})));
      if (!this.controller) return;
      this.transitioning = false;
      if (full) { clearTimeout(this.idleTimer); this.classList.add('is-idle'); }
      else window.AcLayerManager?.release(this.layerId);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.motionObserver?.disconnect();
      this.motion?.revert();
      this.motion = null;
      this.trigger = null;
      for (const video of this.querySelectorAll('video')) video.pause();
      clearTimeout(this.idleTimer);
      cancelAnimationFrame(this.cursorFrame);
      window.AcLayerManager?.release(this.layerId, { restoreFocus: false });
      this.classList.remove('is-enhanced', 'is-motion', 'is-revealed', 'is-playing', 'is-motion-pending', 'is-full', 'is-idle', 'has-pointer');
      this.full = false;
      this.transitioning = false;
      delete this.dataset.filmSwitching;
      for (const span of this.querySelectorAll('.ac-campaign-film__text')) span.replaceWith(...span.childNodes);
      for (const panel of this.panels || []) {
        panel.inert = false;
        panel.removeAttribute('aria-hidden');
        panel.querySelector('.ac-campaign-film__frame').removeAttribute('role');
        panel.querySelector('.ac-campaign-film__frame').removeAttribute('aria-modal');
        panel.querySelector('.ac-campaign-film__frame').removeAttribute('aria-keyshortcuts');
        const video = panel.querySelector('.ac-campaign-film__video');
        if (video.dataset.fullSrc) video.src = video.dataset.fullSrc;
        video.controls = Boolean(video.getAttribute('src'));
        video.muted = true;
      }
    }
  }
  customElements.define('ac-campaign-film', ACCampaignFilm);
})();
